// The photo surfaces of PRD-0031 slices 03 and 06: a person's photos, and every
// face of one document with its state (the overlay's face layer and its faces
// section).
import type { CaseRole } from './cases'
import { apiClient } from './client'

/** One photo of the person — the document plus the face that vouches for it. */
export interface IdentityPhoto {
  document_id: string
  document_name: string | null
  face_id: string
  /** The stored page index, 0 for a photo or a recording: `facePageNumber` names it. */
  page: number | null
  /** The face crop, for the grid thumbnail. */
  crop_url: string | null
  /** The image the box belongs to — for a PDF, the stored page render. */
  source_url: string | null
  box: number[]
  uploaded_at?: string | null
  processed_at?: string | null
}

export interface IdentityPhotosPage {
  photos: IdentityPhoto[]
  total: number
  page: number
  page_size: number
}

/** What the analyst has decided about one detected face. */
export type DocumentFaceState = 'confirmed' | 'suggested' | 'unassigned'

/**
 * One stored suggestion for a face. The evidence fields are named exactly as the
 * candidate table's are, so `matchEvidence` and the match components render them
 * without a second shape (ADR-0040).
 */
export interface DocumentFaceCandidate {
  person_id: string
  display_name: string | null
  /** The person's main photo, for the candidate thumbnail. */
  photo_url: string | null
  score: number
  score_second: number | null
  rival_score: number | null
  reference_count: number
  suggested_match: boolean
  unmet_conditions: string[]
}

/** One detected face of a document with whatever is known about who it is. */
export interface DocumentFacePerson {
  face_id: string
  /** The stored page index, 0 for a photo or a recording: `facePageNumber` names it. */
  page: number | null
  box: number[]
  crop_url: string | null
  /** The image the box belongs to — for a PDF, the stored page render. */
  source_url: string | null
  state: DocumentFaceState
  /** The confirmed person; null for a suggested or unassigned face. */
  person_id: string | null
  display_name: string | null
  /** The confirmed person's main photo, for the rail thumbnail. */
  photo_url: string | null
  /**
   * Up to three stored candidates, strongest first; empty for an unassigned face.
   * On a confirmed face each one is another identity the face's person may be —
   * a merge proposal (ADR-0042).
   */
  candidates: DocumentFaceCandidate[]
  /** Why the crop may be a weak face — the section collapses the flagged ones. */
  quality_flags: string[]
  /** `video` for a face that is one pass of a recording (PRD-0074); null or absent for a photo's. */
  source?: string | null
  /** The pass: the engine's track and its times in seconds of the recording; absent for a photo's face. */
  track_id?: number | null
  t_start?: number | null
  t_end?: number | null
  t_best?: number | null
  /** What the vision model saw of the person on a recording's pass; absent from an older API. */
  description?: string | null
}

export interface DocumentFacesResponse {
  faces: DocumentFacePerson[]
  /**
   * The caller's role as the read was granted it (a superuser reads as owner). A
   * viewer decides nothing about a face (PRD-0032 SC-036). Absent from an older API.
   */
  member_role?: CaseRole
}

export const photosApi = {
  identityPhotos: (
    caseId: string,
    personId: string,
    query: { page?: number; pageSize?: number } = {}
  ) =>
    apiClient.get<IdentityPhotosPage>(
      `/v1/cases/${caseId}/identities/${encodeURIComponent(personId)}/photos`,
      { params: { page: query.page, page_size: query.pageSize } }
    ),

  documentFaces: (caseId: string, documentId: string) =>
    apiClient.get<DocumentFacesResponse>(
      `/v1/cases/${caseId}/documents/${encodeURIComponent(documentId)}/faces`
    ),
}
